<template>
  <div
    id="messages-block"
    :class="selectedConversationIdRef || isNewConversationRef ? 'has-selected-conversation' : ''"
  >
    <EmptyContent v-if="!selectedConversationIdRef && !isNewConversationRef" :text="t('chat.conversationsList.select')">
      <template #icon>
        <NoConversationSelectedIcon color="black" />
      </template>
    </EmptyContent>
    <LoadingContent v-else-if="isConversationPendingRef && selectedConversationIdRef" />
    <div v-else class="messages-block__content">
      <header class="messages-block-header">
        <Button
          variant="ghost"
          size="icon"
          class="messages-block-header__back-btn"
          :aria-label="t('ui.button.back')"
          @click="closeCurrentChat"
        >
          <ChevronLeft class="size-5" aria-hidden="true" />
        </Button>
        <Avatar class="hidden size-10 sm:flex" data-test-id="conversation-avatar">
          <AvatarFallback v-if="isGroupRef">
            <GroupIcon color="black" filled />
          </AvatarFallback>
          <template v-else-if="correspondentRef">
            <AvatarImage v-if="correspondentPictureRef" :src="correspondentPictureRef" alt="" />
            <AvatarFallback class="text-xs font-semibold uppercase">{{ correspondentInitialsRef }}</AvatarFallback>
          </template>
        </Avatar>
        <div class="messages-block-header__identity">
          <div class="messages-block-header__name-row">
            <h2 class="messages-block-header__title min-w-0 truncate" data-test-id="conversation-name">
              <template v-if="isGroupRef"> {{ groupRef?.name }} </template>
              <template v-else-if="correspondentRef">
                {{ correspondentRef?.firstName }} {{ correspondentRef?.lastName }}
              </template>
            </h2>
            <Badge
              v-for="role in headerRolesRef"
              :key="role"
              variant="secondary"
              class="hidden shrink-0 sm:inline-flex"
              data-test-id="conversation-role-badge"
            >
              <span v-if="isGroupRef" class="sr-only">{{ t('chat.header.yourRole') }}</span>
              {{ t(userRolesNamesMap[role]) }}
            </Badge>
          </div>
          <p
            v-if="headerSubtitleRef"
            class="messages-block-header__subtitle truncate"
            data-test-id="conversation-subtitle"
          >
            {{ headerSubtitleRef }}
          </p>
        </div>
        <Button
          v-if="selectedConversationIdRef"
          variant="ghost"
          size="icon"
          class="shrink-0"
          :aria-label="t('chat.messageSearch.open')"
          :aria-pressed="isMessageSearchOpenRef"
          data-test-id="conversation-search-button"
          @click="toggleMessageSearch"
        >
          <Search class="size-5" aria-hidden="true" />
        </Button>
        <Button
          v-if="
            isGroupRef &&
            (groupRolesRef?.includes(RoleEnumTypeV2.MENTOR) ||
              (groupRolesRef?.includes(RoleEnumTypeV2.MENTEE) && menteeCanBookSessionRef))
          "
          variant="outline"
          size="sm"
          class="ml-auto shrink-0"
          data-test-id="conversation-plan-session"
          @click="openNewSessionModal"
        >
          <CalendarPlus aria-hidden="true" />
          <span class="messages-block-header__plan-label">{{ t('ui.layout.nav.newSession') }}</span>
        </Button>
        <!-- tant que le contexte n'a pas sa colonne, il s'ouvre en tiroir -->
        <Button
          v-if="isGroupRef"
          variant="ghost"
          size="icon"
          class="messages-block-header__context-btn shrink-0"
          :aria-label="t('chat.context.open')"
          data-test-id="conversation-context-button"
          @click="openChatContextSheet"
        >
          <PanelRight class="size-5" aria-hidden="true" />
        </Button>
      </header>
      <ConversationSearchBar
        v-if="isMessageSearchOpenRef"
        :feed="feedRef as HTMLElement | null"
        :messages-count="conversationRef?.messages?.length ?? 0"
        @close="closeMessageSearch"
      />
      <div class="messages-block-feed" ref="feedRef">
        <p v-if="showPrivacyNoticeRef" class="messages-block-feed__notice" data-test-id="chat-privacy-notice">
          <ShieldCheck class="size-4 shrink-0" aria-hidden="true" />
          <span>{{ t('chat.privacyNotice.text') }}</span>
          <button
            type="button"
            class="messages-block-feed__notice-close"
            :aria-label="t('chat.privacyNotice.dismiss')"
            @click="dismissPrivacyNotice"
          >
            <X class="size-4" aria-hidden="true" />
          </button>
        </p>
        <div
          v-for="message in conversationRef?.messages"
          :key="message.id"
          :data-message-id="message.id"
          :class="{ 'is-grouped-with-previous': messageGroupingRef[message.id]?.withPrevious }"
        >
          <div v-if="daySeparatorsRef[message.id]" class="messages-block-feed__day" data-test-id="chat-day-separator">
            <time :datetime="getDayKey(message.sentAt) ?? undefined">{{ daySeparatorsRef[message.id] }}</time>
          </div>
          <Message
            v-if="isSessionWidgetMessage(message.content) && isSessionsBatchReadyRef"
            :message="message"
            :groupId="conversationRef?.groupId || ''"
            :conversationUsers="conversationRef?.users || []"
          >
            <template #body>
              <MessageSessionWidget :sessionId="getSessionIdFromMessage(message.content)" />
            </template>
          </Message>
          <Message
            v-else-if="!isSessionWidgetMessage(message.content)"
            :grouped-with-previous="messageGroupingRef[message.id]?.withPrevious"
            :grouped-with-next="messageGroupingRef[message.id]?.withNext"
            :message="message"
            :groupId="conversationRef?.groupId || ''"
            :conversationUsers="conversationRef?.users || []"
          />
        </div>
        <div class="first-message" v-if="isFirstMessageNotSentRef" data-test-id="chat-empty-state">
          <span class="first-message__icon" aria-hidden="true"><MessageCircle class="size-5" /></span>
          <p class="first-message__title">{{ t('chat.empty.title') }}</p>
          <span data-test-id="no-message-yet" class="first-message-text">{{
            t('chat.conversation.noMessagesSentYetInGroup')
          }}</span>
          <Button type="button" variant="outline" size="sm" data-test-id="chat-introduce-btn" @click="introduceMyself">
            <Hand aria-hidden="true" />
            {{ t('chat.empty.introduce') }}
          </Button>
        </div>
        <ConversationSeenBy v-if="conversationRef" :conversation="conversationRef" />
      </div>
      <div
        v-if="isProgrammeEndedRef"
        class="messages-block-programme-ended"
        role="status"
        data-test-id="chat-programme-ended"
      >
        <Archive class="size-4 shrink-0" aria-hidden="true" />
        <span>{{ t('chat.programmeEnded.text', { date: programEndDateRef }) }}</span>
      </div>
      <div class="messages-block-footer">
        <!-- fichiers joints pas encore envoyes : liste repliable (maquette, proposition D) -->
        <ComposerAttachments
          :items="composerAttachmentsRef"
          @remove="removeComposerAttachment"
          @retry="retryFailedUpload"
        />
        <div class="messages-block-footer__btns-wrapper">
          <div
            class="messages-block-footer__btns-wrapper__first-message-generation"
            v-if="
              isFirstMessageNotSentRef &&
              isFirstGroupMessageLoadingRef &&
              !isFirstGroupMessageErrorRef &&
              !hasUserStartedTypingRef
            "
          >
            <LoadingIcon color="primary" />
            <span data-test-id="generate-message">{{ t('chat.conversation.generatingAIMessage') }}</span>
          </div>
          <div class="textarea-wrapper" ref="textareaWrapperRef">
            <CustomTextarea
              ref="inputMessageRef"
              :label="t('chat.conversation.placeholder.message')"
              hide-label
              :placeholder="t('chat.conversation.placeholder.message')"
              id="send-message"
              data-test-id="send-message-area"
              type="textarea"
              :is-disabled="isFirstGroupMessageErrorRef"
              :errors="isOverCharLimit ? t('chat.conversation.error.messageTooLong') : undefined"
              @emit:change="onChange"
              @emit:onkeydown="onKeyDown"
              @emit:onkeyup="onKeyUp"
              :value="messageRef"
              :on-change-debounce-delay="0"
            />
            <MentionDropdown
              v-if="showMentionDropdownRef"
              :show="showMentionDropdownRef"
              :users="(taggableUsersRef as TaggableUserType[]) || []"
              :position="mentionDropdownPositionRef"
              @select-user="onSelectMention"
              ref="mentionDropdownRef"
            />
            <div
              v-if="shouldShowCharCounter"
              class="char-counter"
              :class="{ 'over-limit': isOverCharLimit }"
              data-test-id="char-counter"
            >
              {{ messageCharCount }}/{{ MESSAGE_CHAR_LIMIT }}
            </div>
          </div>
          <div class="messages-block-footer__toolbar">
            <Button
              type="button"
              variant="outline"
              size="icon"
              class="messages-block-footer__attach-btn"
              data-test-id="chat-file-upload-btn"
              :aria-label="t('chat.conversation.attachFile')"
              :disabled="!selectedConversationIdRef"
              @click="triggerFileUpload"
            >
              <Paperclip class="size-5" aria-hidden="true" />
            </Button>
            <input
              ref="fileInputRef"
              type="file"
              multiple
              style="display: none"
              :aria-label="t('chat.conversation.attachFile')"
              @change="onFileChange"
              data-test-id="chat-file-input"
            />
            <Button
              type="button"
              class="messages-block-footer__send-btn"
              data-test-id="submit-send-message"
              :aria-label="t('ui.button.send')"
              :aria-busy="isPendingRef || undefined"
              :disabled="
                isPendingRef ||
                isOverCharLimit ||
                !canSendMessageRef ||
                isUploadingFilesRef ||
                hasUnuploadedFilesRef ||
                hasFailedUploadsRef
              "
              @click="sendMessageHandler"
            >
              <span class="messages-block-footer__send-btn__label">{{ t('ui.button.send') }}</span>
              <Loader2 v-if="isPendingRef" class="size-4 animate-spin" aria-hidden="true" />
              <SendHorizontal v-else class="size-4" aria-hidden="true" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useMutation } from '@tanstack/vue-query'
import {
  Archive,
  CalendarPlus,
  ChevronLeft,
  Hand,
  Loader2,
  MessageCircle,
  PanelRight,
  Paperclip,
  Search,
  SendHorizontal,
  ShieldCheck,
  X,
} from 'lucide-vue-next'
import { computed, onBeforeUnmount, ref, watch, watchEffect } from 'vue'
import type { VNodeRef } from 'vue/types/vnode'
import { useRouter } from 'vue-router'
import { toast } from 'vue-sonner'

import MentionDropdown from './mention-dropdown/mention-dropdown.vue'
import MessageSessionWidget from './message-session-widget/message-session-widget.vue'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import useAccount from '@/features/auth/hooks/use-account.hook'
import { RoleEnumTypeV2 } from '@/features/auth/types'
import {
  getChatFilePresignedURLMutation,
  markConversationAsReadMutation,
  sendMessageMutation,
} from '@/features/chat/api'
import ComposerAttachments from '@/features/chat/components/messages-block/composer-attachments.vue'
import ConversationSearchBar from '@/features/chat/components/messages-block/conversation-search-bar.vue'
import ConversationSeenBy from '@/features/chat/components/messages-block/conversation-seen-by/conversation-seen-by.vue'
import Message from '@/features/chat/components/messages-block/message/message.vue'
import { useMentions } from '@/features/chat/composables/use-mentions.composable'
import { useTemporaryConversations } from '@/features/chat/composables/use-temporary-conversations.composable'
import useChatContextSheet from '@/features/chat/hooks/use-chat-context-sheet.hook'
import useConversation from '@/features/chat/hooks/use-conversation.hook'
import useConversations from '@/features/chat/hooks/use-conversations.hook'
import { useFirstGroupMessage } from '@/features/chat/hooks/use-first-group-message.hook'
import useGroupConversationsWithoutFirstMessage from '@/features/chat/hooks/use-group-conversations-without-first-message.hook'
import type { GetChatFilePresignedURLResponseType, TaggableUserType } from '@/features/chat/types'
import type { SendMessageDTOType } from '@/features/chat/types'
import type { ComposerAttachmentType } from '@/features/chat/utils/composer-attachments'
import { getMessageGrouping } from '@/features/chat/utils/message-grouping'
import { checkFileImportationMutation, deleteFileMutation } from '@/features/files/api'
import {
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_LABEL,
  toastFileUploadError,
  uploadFileToPresignedUrl,
} from '@/features/files/utils'
import useGroup from '@/features/groups/hooks/use-group.hook'
import groupsStore from '@/features/groups/stores/groups/groups.store'
import useSessionsBatch from '@/features/sessions/hooks/use-sessions-batch.hook'
import CustomTextarea from '@/features/ui/components/custom-textarea/custom-textarea.vue'
import EmptyContent from '@/features/ui/components/empty-content/empty-content.vue'
import GroupIcon from '@/features/ui/components/icons/group-icon.vue'
import LoadingIcon from '@/features/ui/components/icons/loading-icon.vue'
import NoConversationSelectedIcon from '@/features/ui/components/icons/no-conversation-selected-icon.vue'
import LoadingContent from '@/features/ui/components/loading-content/loading-content.vue'
import useDashboardData from '@/features/ui/pages/dashboard/hooks/use-dashboard-data.hook'
import modalsStore from '@/features/ui/store/modal/modal.store'
import useGroupRoles from '@/features/users/hooks/use-group-roles.hook'
import useUser from '@/features/users/hooks/use-user.hook'
import { userRolesNamesMap } from '@/features/users/utils'
import { useI18n } from '@/lib/i18n'
import { selectGetCurrentRoute } from '@/store/route/route.selector'
import routeStore from '@/store/route/route.store'
import { PROFILE_PICTURE_BUCKET_URL, ROUTES } from '@/utils/config/constants'
import { getDate, getDayKey, getDayLabel } from '@/utils/dates'
import { invalidateConversations, invalidateSessionObjectives } from '@/utils/lib/vue-query'

const { t } = useI18n()
const router = useRouter()
const currentRouteRef = selectGetCurrentRoute(routeStore)

// Message character limit
const MESSAGE_CHAR_LIMIT = 5000
const CHAR_COUNTER_THRESHOLD = 4900

const { accountRef } = useAccount()

const selectedConversationIdRef = computed(() => {
  const roomId = (currentRouteRef.value?.params?.roomId as string) ?? null
  return roomId
})

const sendToUserIdRef = computed(() => {
  return currentRouteRef.value?.query?.sendTo as string | undefined
})
const sendToProgramIdRef = computed(() => {
  return currentRouteRef.value?.query?.programId as string | undefined
})
const isNewConversationRef = computed(() => {
  return !selectedConversationIdRef.value && !!sendToUserIdRef.value && !!sendToProgramIdRef.value
})

const { conversationRef, isPendingRef: isConversationPendingRef } = useConversation(selectedConversationIdRef)
const { conversationsRef } = useConversations()

const sendToUserRef = computed(() => sendToUserIdRef.value)
const { userRef: newConversationUserRef } = useUser(sendToUserRef)
const selectedGroupIdRef = computed(() => conversationRef.value?.groupId ?? null)
const { groupRef } = useGroup(selectedGroupIdRef)
const { groupRolesRef } = useGroupRoles({ groupIdRef: selectedGroupIdRef })

// get all the groups without the first message sent and check if the current conversation is one of them
const { groupConversationsWithoutFirstMessageSentRef } = useGroupConversationsWithoutFirstMessage()
const isFirstMessageNotSentRef = computed(() =>
  groupConversationsWithoutFirstMessageSentRef.value.some((c) => c.conversationId === selectedConversationIdRef.value),
)
import useProgram from '@/features/programs/hooks/use-program.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'

const hasUserStartedTypingRef = ref(false)

const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)
// reglages du programme de la conversation ouverte (chat tous programmes)
const chatProgramIdRef = computed(
  () => conversationRef.value?.programId ?? sendToProgramIdRef.value ?? selectedProgramIdRef.value ?? null,
)
const { programRef } = useProgram(chatProgramIdRef)
const menteeCanBookSessionRef = computed(() => programRef?.value?.settings.menteeCanBookSession)

const hasMessageBeenSentRef = ref(false)
const temporaryConversationIdRef = ref<string | null>(null)
const temporaryConversationSendToRef = ref<string | null>(null)
const temporaryConversationProgramIdRef = ref<string | null>(null)
const { createTemporaryConversation, removeTemporaryConversation, convertTemporaryToReal, isTemporaryConversation } =
  useTemporaryConversations()

// Mentions functionality
const {
  showMentionDropdown,
  mentionSearchQuery,
  mentionStartIndex,
  taggableUsersRef,
  isLoadingTaggableUsersRef,
  insertMention,
  checkForMention,
  formatMentionForDisplay,
  convertMentionsForSend,
  findMentionPosition,
} = useMentions(selectedConversationIdRef)

const showMentionDropdownRef = computed(() => showMentionDropdown.value && selectedConversationIdRef.value !== null)
const mentionDropdownPositionRef = ref({ bottom: 0, left: 0 })

// watch for conversation changes and clear the input
watch(selectedConversationIdRef, (newVal, oldVal) => {
  if (newVal && inputMessageRef.value) {
    inputMessageRef.value.clear()
    messageRef.value = null
    messageInternalRef.value = null
    mentionMapRef.value.clear()
  }

  if (
    temporaryConversationIdRef.value &&
    oldVal === temporaryConversationIdRef.value &&
    newVal !== temporaryConversationIdRef.value &&
    !hasMessageBeenSentRef.value &&
    isTemporaryConversation(temporaryConversationIdRef.value)
  ) {
    removeTemporaryConversation(temporaryConversationIdRef.value)
    temporaryConversationIdRef.value = null
  }

  if (newVal !== oldVal) {
    if (newVal !== temporaryConversationIdRef.value) {
      temporaryConversationIdRef.value = null
      temporaryConversationSendToRef.value = null
      temporaryConversationProgramIdRef.value = null
      hasMessageBeenSentRef.value = false
    }
  }
})

watchEffect(() => {
  if (
    isNewConversationRef.value &&
    sendToUserIdRef.value &&
    sendToProgramIdRef.value &&
    newConversationUserRef.value &&
    accountRef.value &&
    !temporaryConversationIdRef.value
  ) {
    const existingConversation = conversationsRef.value.find((conv) => {
      if (!conv.groupId) {
        const userIds = conv.users.map((u) => u.id)
        return (
          userIds.includes(accountRef.value?.id || '') &&
          userIds.includes(sendToUserIdRef.value!) &&
          userIds.length === 2
        )
      }
      return false
    })

    if (existingConversation) {
      router.push(ROUTES.CHAT(existingConversation.conversationId))
      return
    }
    const tempId = createTemporaryConversation({
      sendToUserId: sendToUserIdRef.value,
      sendToUser: {
        id: newConversationUserRef.value.id!,
        firstName: newConversationUserRef.value.firstName || '',
        lastName: newConversationUserRef.value.lastName || '',
        picture: newConversationUserRef.value.picture || null,
        roles: newConversationUserRef.value.roles || [],
      },
      programId: sendToProgramIdRef.value,
      currentUser: {
        id: accountRef.value.id!,
        firstName: accountRef.value.firstName || '',
        lastName: accountRef.value.lastName || '',
        picture: accountRef.value.picture || null,
        roles: accountRef.value.roles || [],
      },
    })

    temporaryConversationIdRef.value = tempId
    temporaryConversationSendToRef.value = sendToUserIdRef.value
    temporaryConversationProgramIdRef.value = sendToProgramIdRef.value
    hasMessageBeenSentRef.value = false

    router.push(ROUTES.CHAT(tempId))
  }
})

// adjust text area as we type (create fake div to get the height of the text)
function adjustTextAreaHeight() {
  if (!inputMessageRef.value || !inputMessageRef.value.$el) {
    return
  }
  const textArea = inputMessageRef.value.$el.querySelector('textarea')
  if (textArea) {
    const div = document.createElement('div')
    div.style.whiteSpace = 'pre-wrap'
    div.textContent = textArea.value
    div.style.width = textArea.offsetWidth + 'px'

    document.documentElement.appendChild(div)

    textArea.style.height = `${div.scrollHeight + 20}px`
    div.remove()
  }
}

// if it's the case, get the first message generated by the AI
const {
  firstGroupMessageRef,
  isLoadingRef: isFirstGroupMessageLoadingRef,
  isErrorRef: isFirstGroupMessageErrorRef,
} = useFirstGroupMessage(selectedConversationIdRef, isFirstMessageNotSentRef)

// watch for the first mentorship group message to be generated (AI), set it in the input
// if the user hasn't started typing yet, we set the message in the input
watch(firstGroupMessageRef, (newVal) => {
  if (hasUserStartedTypingRef.value) {
    return
  }
  if (newVal?.message) {
    messageRef.value = newVal.message
    inputMessageRef.value.set(newVal.message)
    // adjust the height after the message is set
    setTimeout(() => {
      adjustTextAreaHeight()
    }, 5)
  } else {
    messageRef.value = null
    inputMessageRef.value?.clear()
  }
})

const isGroupRef = computed(() => conversationRef.value?.groupId)
const correspondentRef = computed(() => {
  if (isNewConversationRef.value && newConversationUserRef.value) {
    return {
      id: newConversationUserRef.value.id,
      firstName: newConversationUserRef.value.firstName,
      lastName: newConversationUserRef.value.lastName,
      picture: newConversationUserRef.value.picture,
    }
  }
  return conversationRef.value?.users?.find((user) => user.id !== accountRef.value?.id)
})

const correspondentInitialsRef = computed(
  () => `${correspondentRef.value?.firstName?.[0] ?? ''}${correspondentRef.value?.lastName?.[0] ?? ''}`,
)
const correspondentPictureRef = computed(() =>
  correspondentRef.value?.picture ? PROFILE_PICTURE_BUCKET_URL + correspondentRef.value.picture : undefined,
)

// en-tete : role (le mien en groupe, celui du correspondant en prive) et sous-titre du binome
const DISPLAYED_ROLES = [RoleEnumTypeV2.MENTOR, RoleEnumTypeV2.MENTEE]
const { openChatContextSheet } = useChatContextSheet()

const headerRolesRef = computed<RoleEnumTypeV2[]>(() => {
  if (isGroupRef.value) return (groupRolesRef.value ?? []).filter((role) => DISPLAYED_ROLES.includes(role))
  const correspondentUser = conversationRef.value?.users?.find((user) => user.id === correspondentRef.value?.id)
  return ((correspondentUser?.roles ?? []) as RoleEnumTypeV2[]).filter((role) => DISPLAYED_ROLES.includes(role))
})

const { objectivesRef: headerObjectivesRef } = useDashboardData({ selectedGroupIdRef })
const headerSubtitleRef = computed(() => {
  if (!isGroupRef.value || !groupRef.value) return null
  const members = groupRef.value.users
    .filter((user) => user.id !== accountRef.value?.id)
    .map((user) => user.firstName)
    .join(', ')
  const expected = headerObjectivesRef.value.reduce((sum, objective) => sum + objective.expectedSessionCount, 0)
  const done = headerObjectivesRef.value.reduce(
    (sum, objective) => sum + Math.min(objective.completedSessionsCount, objective.expectedSessionCount),
    0,
  )
  const sessions =
    expected > 0
      ? t('chat.header.sessionsProgress', { done, expected })
      : t('chat.header.sessionsCount', { count: groupRef.value.completedSessions })
  return [groupProgramRef.value?.name, members, sessions].filter(Boolean).join(' · ')
})

// programme termine : simple rappel au-dessus du composeur, l'envoi reste possible
const groupProgramIdRef = computed(() => groupRef.value?.programId ?? null)
const { programRef: groupProgramRef } = useProgram(groupProgramIdRef)
const programEndAtRef = computed(() => groupProgramRef.value?.latestPeriod?.endAt ?? null)
const isProgrammeEndedRef = computed(
  () => !!isGroupRef.value && !!programEndAtRef.value && new Date(programEndAtRef.value).getTime() < Date.now(),
)
const programEndDateRef = computed(() => (programEndAtRef.value ? getDate(programEndAtRef.value) : ''))

// rappel de confidentialite, affiche tant que l'utilisateur ne l'a pas masque
const PRIVACY_NOTICE_STORAGE_KEY = 'constel.chat.privacyNoticeDismissed'
const readPrivacyNoticeDismissed = () => {
  try {
    return window.localStorage.getItem(PRIVACY_NOTICE_STORAGE_KEY) === '1'
  } catch {
    return false
  }
}
const isPrivacyNoticeDismissedRef = ref(readPrivacyNoticeDismissed())
const showPrivacyNoticeRef = computed(() => !isPrivacyNoticeDismissedRef.value && !!conversationRef.value)
function dismissPrivacyNotice() {
  isPrivacyNoticeDismissedRef.value = true
  try {
    window.localStorage.setItem(PRIVACY_NOTICE_STORAGE_KEY, '1')
  } catch {
    // stockage indisponible : le rappel reviendra a la prochaine visite
  }
}

// etat vide : "Me presenter" remet le message de presentation genere et place le focus dans le champ
function introduceMyself() {
  const generatedMessage = firstGroupMessageRef.value?.message
  if (generatedMessage && !messageRef.value) {
    messageRef.value = generatedMessage
    inputMessageRef.value?.set(generatedMessage)
    setTimeout(() => adjustTextAreaHeight(), 5)
  }
  textareaWrapperRef.value?.querySelector('textarea')?.focus()
}

const feedRef = ref<VNodeRef | null>(null)
// recherche dans la conversation ouverte (loupe de l'en-tete)
const isMessageSearchOpenRef = ref(false)
const toggleMessageSearch = () => {
  isMessageSearchOpenRef.value = !isMessageSearchOpenRef.value
}
const closeMessageSearch = () => {
  isMessageSearchOpenRef.value = false
}
watch(selectedConversationIdRef, closeMessageSearch)
const inputMessageRef = ref<VNodeRef | null>(null)
const textareaWrapperRef = ref<HTMLElement | null>(null)
const mentionDropdownRef = ref<InstanceType<typeof MentionDropdown> | null>(null)
const messageRef = ref<string | null>(null)
const textareaCursorPositionRef = ref(0)
const fileInputRef = ref<HTMLInputElement | null>(null)

// Files management
const filesRef = ref<File[]>([])
const chatFilesRef = ref<GetChatFilePresignedURLResponseType[]>([])
const isFilesUploadingRef = ref<{ isUploading: boolean; fileId: string }[]>([])
const uploadPercentagesRef = ref<{ percentage: number; fileId: string }[]>([])
const xhrsRef = ref<{ xhr: XMLHttpRequest; fileId: string }[]>([])

const isUploadingFilesRef = computed(() => isFilesUploadingRef.value.some((item) => item.isUploading))
// envois en echec : gardes dans la liste avec « Reessayer » (le fichier local est conserve)
const failedUploadsRef = ref<{ id: string; file: File }[]>([])
const hasFailedUploadsRef = computed(() => failedUploadsRef.value.length > 0)
// taille des fichiers locaux, affichee dans la liste des fichiers joints
const fileSizesRef = ref<Record<string, number>>({})
const composerAttachmentsRef = computed<ComposerAttachmentType[]>(() => [
  ...chatFilesRef.value.map((file) => ({
    id: file.id,
    name: file.name,
    ext: file.ext,
    size: fileSizesRef.value[file.id] ?? null,
    status: isFilesUploadingRef.value.find((i) => i.fileId === file.id)?.isUploading
      ? ('uploading' as const)
      : ('ready' as const),
    percentage: uploadPercentagesRef.value.find((i) => i.fileId === file.id)?.percentage ?? 0,
  })),
  ...failedUploadsRef.value.map(({ id, file }) => ({
    id,
    name: file.name,
    size: file.size,
    status: 'error' as const,
  })),
])
const hasUnuploadedFilesRef = computed(() => {
  return chatFilesRef.value.some((file) => {
    const uploading = isFilesUploadingRef.value.find((i) => i.fileId === file.id)
    return uploading?.isUploading || !file.isImported
  })
})

const hasImportedFilesRef = computed(() => {
  return chatFilesRef.value.filter((f) => f.isImported).length > 0
})

const canSendMessageRef = computed(() => {
  const hasMessage = messageRef.value?.trim().length > 0
  return hasMessage || hasImportedFilesRef.value
})

// Character count for message input
const messageCharCount = computed(() => messageRef.value?.length || 0)
const isOverCharLimit = computed(() => messageCharCount.value > MESSAGE_CHAR_LIMIT)
const shouldShowCharCounter = computed(() => messageCharCount.value >= CHAR_COUNTER_THRESHOLD)

const { mutate: markConversationAsRead } = useMutation({
  mutationFn: markConversationAsReadMutation,
  onSuccess: () => {
    invalidateConversations()
  },
})

// séparateurs de jour : id du premier message de chaque jour -> libellé
const daySeparatorsRef = computed(() => {
  const labels: Record<string, string> = {}
  const dayLabels = { today: t('chat.conversation.today'), yesterday: t('chat.conversation.yesterday') }
  let previousDay: string | null = null
  for (const message of conversationRef.value?.messages ?? []) {
    const day = getDayKey(message.sentAt)
    if (day && day !== previousDay) labels[message.id] = getDayLabel(message.sentAt, dayLabels)
    previousDay = day
  }
  return labels
})

// blocs de messages (meme auteur, moins de 5 minutes) : voir message-grouping.ts
const messageGroupingRef = computed(() =>
  getMessageGrouping(conversationRef.value?.messages ?? [], {
    isStandalone: (message) => isSessionWidgetMessage(message.content),
    startsNewDay: (message) => !!daySeparatorsRef.value[message.id],
  }),
)

function isSessionWidgetMessage(messageContent: string) {
  return messageContent.startsWith('***session=')
}

function getSessionIdFromMessage(messageContent: string) {
  return messageContent.split('***session=')[1]
}

const conversationSessionIdsRef = computed(() => {
  const messages = conversationRef.value?.messages ?? []
  const ids = new Set<string>()
  for (const message of messages) {
    if (isSessionWidgetMessage(message.content)) {
      const id = getSessionIdFromMessage(message.content)
      if (id) ids.add(id)
    }
  }
  return [...ids]
})
const { isReadyRef: isSessionsBatchReadyRef } = useSessionsBatch(conversationSessionIdsRef)

// closing the current chat on mobile device (arrow)
function closeCurrentChat() {
  router.push('/chat')
}

// Store internal format (with @[userId|userName])
const messageInternalRef = ref<string | null>(null)

// Map to track mentions: key is display text "@Name", value is userId
const mentionMapRef = ref<Map<string, string>>(new Map())

// Rebuild internal format from display format using mention map
function rebuildInternalFormat(displayText: string): string {
  let internalText = displayText
  mentionMapRef.value.forEach((userId, displayMention) => {
    // Escape special regex characters
    const escaped = displayMention.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const regex = new RegExp(escaped, 'g')
    internalText = internalText.replace(regex, `@[${userId}|${displayMention.substring(1)}]`)
  })
  return internalText
}

function onChange(newVal: string) {
  hasUserStartedTypingRef.value = true

  const textarea = inputMessageRef.value?.$el.querySelector('textarea') as HTMLTextAreaElement | null
  if (!textarea) return

  const hasInternalFormat = /@\[[a-f0-9-]+\|[^\]]+\]/.test(newVal)

  if (hasInternalFormat) {
    const formatted = formatMentionForDisplay(newVal)
    if (formatted !== newVal) {
      const cursorPos = textarea.selectionStart
      textarea.value = formatted
      setTimeout(() => {
        textarea.setSelectionRange(cursorPos, cursorPos)
      }, 0)
      newVal = formatted
    }
  }

  messageRef.value = newVal

  messageInternalRef.value = rebuildInternalFormat(newVal)

  if (newVal) {
    textareaCursorPositionRef.value = textarea.selectionStart
    checkForMention(newVal, textareaCursorPositionRef.value)
    updateMentionDropdownPosition()
  } else {
    showMentionDropdown.value = false
  }
}

function onKeyDown(event: KeyboardEvent) {
  const textarea = event.target as HTMLTextAreaElement
  if (textarea && showMentionDropdown.value && mentionDropdownRef.value) {
    if (event.key === 'Enter' || event.key === 'Tab') {
      event.preventDefault()
      mentionDropdownRef.value.selectCurrent()
      return
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault()
      mentionDropdownRef.value.selectNext()
      return
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault()
      mentionDropdownRef.value.selectPrevious()
      return
    }
    if (event.key === 'Escape') {
      event.preventDefault()
      showMentionDropdown.value = false
      return
    }
  }
}

function onKeyUp(event: KeyboardEvent) {
  adjustTextAreaHeight()

  const textarea = event.target as HTMLTextAreaElement
  if (textarea) {
    textareaCursorPositionRef.value = textarea.selectionStart

    if (messageRef.value) {
      checkForMention(messageRef.value, textareaCursorPositionRef.value)
      updateMentionDropdownPosition()
    }
  }
}

function updateMentionDropdownPosition() {
  if (!textareaWrapperRef.value || !inputMessageRef.value) {
    return
  }

  const textarea = inputMessageRef.value.$el.querySelector('textarea') as HTMLTextAreaElement
  if (!textarea) {
    return
  }

  const gap = 5

  mentionDropdownPositionRef.value = {
    bottom: gap,
    left: 0,
  }
}

function onSelectMention(user: { id: string; fullName: string }) {
  if (!messageRef.value || !inputMessageRef.value) {
    return
  }

  const textarea = inputMessageRef.value.$el.querySelector('textarea') as HTMLTextAreaElement
  if (!textarea) {
    return
  }

  const cursorPos = textareaCursorPositionRef.value
  const currentText = messageRef.value

  const displayMention = `@${user.fullName}`

  mentionMapRef.value.set(displayMention, user.id)

  const before = currentText.substring(0, cursorPos)
  const after = currentText.substring(cursorPos)

  const mentionPos = findMentionPosition(currentText, cursorPos)
  let newDisplayText: string
  let newCursorPosition: number

  if (mentionPos) {
    const beforeMention = currentText.substring(0, mentionPos.start)
    const afterMention = currentText.substring(mentionPos.end)
    newDisplayText = beforeMention + displayMention + ' ' + afterMention
    newCursorPosition = beforeMention.length + displayMention.length + 1
  } else {
    newDisplayText = before + displayMention + ' ' + after
    newCursorPosition = before.length + displayMention.length + 1
  }

  messageRef.value = newDisplayText

  messageInternalRef.value = rebuildInternalFormat(newDisplayText)

  inputMessageRef.value.set(newDisplayText)

  setTimeout(() => {
    if (textarea) {
      textarea.setSelectionRange(newCursorPosition, newCursorPosition)
      textarea.focus()
    }
  }, 0)

  showMentionDropdown.value = false
}

// on new messages, scroll to bottom + mark conversation as read
watch(
  [feedRef, conversationRef],
  ([feed]) => {
    if (feed && !isMessageSearchOpenRef.value) {
      feed.scrollTo({ top: feed.scrollHeight })
    }
    // if conversation selected and the user hasn't seen the conversation yet, mark it as read
    if (
      selectedConversationIdRef.value &&
      conversationRef.value &&
      !conversationRef.value.users.find((u) => u.id === accountRef.value?.id)?.hasSeen
    ) {
      markConversationAsRead({ conversationId: selectedConversationIdRef.value })
    }
  },
  { deep: true, immediate: true, flush: 'post' },
)

function triggerFileUpload() {
  fileInputRef.value?.click()
}

function onFileChange(event: Event) {
  const input = event.target as HTMLInputElement
  if (input.files) {
    Array.from(input.files).forEach((file) => {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        toast?.error(t('error.upload.tooLarge', { maxSize: MAX_FILE_SIZE_LABEL }))
        return
      }
      filesRef.value = [...filesRef.value, file]
    })
    // Reset input
    if (input) {
      input.value = ''
    }
  }
}

const { mutateAsync: getChatFilePresignedURL } = useMutation({
  mutationFn: getChatFilePresignedURLMutation,
})

const { mutateAsync: checkFileImportation } = useMutation({
  mutationFn: checkFileImportationMutation,
})

const { mutate: deleteChatFile } = useMutation({
  mutationFn: deleteFileMutation,
  onError: () => {
    toast?.error(t('error.common'))
  },
  onSuccess: async (data, variables) => {
    chatFilesRef.value = chatFilesRef.value.filter((i) => i.id !== variables.fileId)
    toast?.success(t('chat.conversation.fileDeleted'))
  },
})

async function uploadFileAndTrackProgress(file: File) {
  let uploadData: GetChatFilePresignedURLResponseType | undefined
  try {
    if (filesRef.value.length === 0 || !selectedConversationIdRef.value) {
      return
    }
    filesRef.value = filesRef.value.filter((i) => i.name !== file.name)

    uploadData = await getChatFilePresignedURL({
      mimetype: file.type,
      name: file.name,
      conversationId: selectedConversationIdRef.value,
    })

    chatFilesRef.value = [...chatFilesRef.value, uploadData]
    fileSizesRef.value = { ...fileSizesRef.value, [uploadData.id]: file.size }
    uploadPercentagesRef.value = [...uploadPercentagesRef.value, { percentage: 0, fileId: uploadData.id }]
    isFilesUploadingRef.value = [...isFilesUploadingRef.value, { isUploading: true, fileId: uploadData.id }]

    await uploadFileToPresignedUrl({
      file,
      url: uploadData.url,
      onProgress: (percentage) => {
        uploadPercentagesRef.value = uploadPercentagesRef.value.map((p) =>
          p.fileId === uploadData!.id ? { ...p, percentage } : p,
        )
      },
      onXhrReady: (xhr) => {
        xhrsRef.value = [...xhrsRef.value, { xhr, fileId: uploadData!.id }]
      },
    })

    const importationResult = await checkFileImportation({ fileId: uploadData.id })
    if (importationResult.isImported) {
      chatFilesRef.value = chatFilesRef.value.map((f) => (f.id === uploadData!.id ? { ...f, isImported: true } : f))
    }

    isFilesUploadingRef.value = isFilesUploadingRef.value.map((uploading) => {
      if (uploading.fileId === uploadData!.id) {
        return { ...uploading, isUploading: false }
      }
      return uploading
    })

    xhrsRef.value = xhrsRef.value.filter((i) => i.fileId !== uploadData!.id)
  } catch (error) {
    // annule par l'utilisateur : le fichier a deja ete retire de la liste
    const wasCancelled = !!uploadData && !chatFilesRef.value.some((f) => f.id === uploadData!.id)
    let isFailed = !uploadData
    if (uploadData) {
      try {
        const importationResult = await checkFileImportation({ fileId: uploadData.id })
        if (importationResult.isImported) {
          chatFilesRef.value = chatFilesRef.value.map((f) => (f.id === uploadData!.id ? { ...f, isImported: true } : f))
        } else {
          chatFilesRef.value = chatFilesRef.value.filter((i) => i.id !== uploadData!.id)
          isFailed = true
        }
      } catch {
        chatFilesRef.value = chatFilesRef.value.filter((i) => i.id !== uploadData!.id)
        isFailed = true
      }
      xhrsRef.value = xhrsRef.value.filter((i) => i.fileId !== uploadData.id)
      isFilesUploadingRef.value = isFilesUploadingRef.value.map((uploading) => {
        if (uploading.fileId === uploadData!.id) {
          return { ...uploading, isUploading: false }
        }
        return uploading
      })
    }
    if (wasCancelled) return
    if (isFailed && !failedUploadsRef.value.some((f) => f.file === file)) {
      failedUploadsRef.value = [...failedUploadsRef.value, { id: `failed-${crypto.randomUUID()}`, file }]
    }
    toastFileUploadError(error, toast, t)
    console.error('Error uploading file:', error)
  }
}

watch(filesRef, () => {
  filesRef.value.forEach((file) => {
    uploadFileAndTrackProgress(file)
  })
})

function removeComposerAttachment(id: string) {
  if (failedUploadsRef.value.some((f) => f.id === id)) {
    failedUploadsRef.value = failedUploadsRef.value.filter((f) => f.id !== id)
    return
  }
  removeChatFile(id)
}

// relance l'envoi d'un fichier en echec : il repasse par la file d'envoi
function retryFailedUpload(id: string) {
  const failed = failedUploadsRef.value.find((f) => f.id === id)
  if (!failed) return
  failedUploadsRef.value = failedUploadsRef.value.filter((f) => f.id !== id)
  filesRef.value = [...filesRef.value, failed.file]
}

function removeChatFile(id: string) {
  if (isFilesUploadingRef.value.find((i) => i.fileId === id)?.isUploading) {
    const xhrToAbort = xhrsRef.value.find((i) => i.fileId === id)
    if (xhrToAbort) {
      xhrToAbort.xhr.abort()
    }
    xhrsRef.value = xhrsRef.value.filter((i) => i.fileId !== id)
    chatFilesRef.value = chatFilesRef.value.filter((i) => i.id !== id)
    isFilesUploadingRef.value = isFilesUploadingRef.value.filter((i) => i.fileId !== id)
    toast?.success(t('chat.conversation.fileDeleted'))
    return
  }

  deleteChatFile({ fileId: id })
}

watch(selectedConversationIdRef, () => {
  chatFilesRef.value = []
  filesRef.value = []
  failedUploadsRef.value = []
  fileSizesRef.value = {}
  isFilesUploadingRef.value = []
  uploadPercentagesRef.value = []
  xhrsRef.value = []
})

const { mutate: sendMessage, isPending: isPendingRef } = useMutation({
  mutationFn: sendMessageMutation,
  onSuccess: async (result: any, variables: SendMessageDTOType) => {
    console.log('message sent', result)

    try {
      messageRef.value = null
      if (inputMessageRef.value) {
        inputMessageRef.value.clear()
      }

      hasMessageBeenSentRef.value = true

      const wasNewConversation = !variables.conversationId && !!result?.conversationId
      const wasTemporaryConversation =
        temporaryConversationIdRef.value && isTemporaryConversation(temporaryConversationIdRef.value)

      if (wasTemporaryConversation && temporaryConversationIdRef.value && result?.conversationId) {
        convertTemporaryToReal(result.conversationId, temporaryConversationIdRef.value)
        const realConversationId = result.conversationId
        temporaryConversationIdRef.value = null
        temporaryConversationSendToRef.value = null
        temporaryConversationProgramIdRef.value = null

        try {
          await invalidateConversations()
          await invalidateSessionObjectives()
        } catch (invalidateError) {
          console.warn('Failed to invalidate conversations (non-critical):', invalidateError)
        }
        try {
          await router.push(ROUTES.CHAT(realConversationId))
        } catch (navError) {
          console.error('Navigation error:', navError)
          window.history.replaceState({}, '', ROUTES.CHAT(realConversationId))
        }
      } else if (wasNewConversation && result?.conversationId) {
        try {
          await invalidateConversations()
          await invalidateSessionObjectives()
        } catch (invalidateError) {
          console.warn('Failed to invalidate conversations (non-critical):', invalidateError)
        }
        try {
          await router.push(ROUTES.CHAT(result.conversationId))
        } catch (navError) {
          console.error('Navigation error:', navError)
          window.history.replaceState({}, '', ROUTES.CHAT(result.conversationId))
        }
      } else {
        try {
          await invalidateConversations()
          await invalidateSessionObjectives()
        } catch (invalidateError) {
          console.warn('Failed to invalidate conversations (non-critical):', invalidateError)
        }
      }

      if (result?.pii && programRef.value?.settings?.moderationPiiEnabled) {
        const conversationId = result.conversationId || selectedConversationIdRef.value
        modalsStore.updateModalAttachedData('contact-warning', {
          messageId: result.message.id,
          conversationId: conversationId,
        })
        modalsStore.toggleModal('contact-warning', true)
        console.log('message sent', result.pii)
      }

      // adjust the height after the message is sent
      setTimeout(() => {
        adjustTextAreaHeight()
      }, 5)
    } catch (error) {
      console.error('Error in onSuccess callback:', error)
    }

    messageRef.value = null
    messageInternalRef.value = null
    mentionMapRef.value.clear()
    if (inputMessageRef.value) {
      inputMessageRef.value.clear()
    }
    chatFilesRef.value = []
    filesRef.value = []
    failedUploadsRef.value = []
    fileSizesRef.value = {}
    isFilesUploadingRef.value = []
    uploadPercentagesRef.value = []
    xhrsRef.value = []
    // adjust the height after the message is sent (vérifier que le composant existe)
    setTimeout(() => {
      if (inputMessageRef.value) {
        adjustTextAreaHeight()
      }
    }, 5)
  },
  onError: (error: any) => {
    console.error('Error sending message:', error)
    toast?.error(t('error.common'))
  },
  onSettled: async () => {
    try {
      await invalidateConversations()
    } catch (invalidateError) {
      console.warn('Failed to invalidate conversations in onSettled (non-critical):', invalidateError)
    }
  },
})

// sanitize the message before sending
function sanitizeMessage(message: string) {
  // Use internal format if available, otherwise use display format
  const messageToProcess = messageInternalRef.value || message

  // Convert mentions from internal format (@[userId|userName]) to send format (@userId)
  let processedMessage = convertMentionsForSend(messageToProcess)

  // Then sanitize HTML
  return processedMessage
    .trim()
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

function sendMessageHandler() {
  // do stuff
  const hasMessage = messageRef.value?.length || chatFilesRef.value.some((f) => f.isImported)

  if (!hasMessage) {
    return
  }

  const messageToSend = messageInternalRef.value || messageRef.value

  // Si c'est une conversation temporaire, on envoie avec sendAt et programId (créera la conversation en DB)
  const isTemporary = selectedConversationIdRef.value && isTemporaryConversation(selectedConversationIdRef.value)

  // Utiliser les valeurs stockées pour les conversations temporaires, ou les query params si disponibles
  const sendToUserId = isTemporary ? temporaryConversationSendToRef.value : sendToUserIdRef.value
  const sendToProgramId = isTemporary ? temporaryConversationProgramIdRef.value : sendToProgramIdRef.value

  if (isTemporary && sendToUserId && sendToProgramId) {
    sendMessage({
      message: sanitizeMessage(messageToSend || ''),
      sendAt: sendToUserId,
      programId: sendToProgramId,
      fileIds: chatFilesRef.value.filter((f) => f.isImported).map((f) => f.id),
    })
  } else if (selectedConversationIdRef.value && !isTemporary) {
    sendMessage({
      message: sanitizeMessage(messageToSend || ''),
      conversationId: selectedConversationIdRef.value,
      fileIds: chatFilesRef.value.filter((f) => f.isImported).map((f) => f.id),
    })
  } else if (isNewConversationRef.value) {
    sendMessage({
      message: sanitizeMessage(messageToSend || ''),
      sendAt: sendToUserIdRef.value!,
      programId: sendToProgramIdRef.value!,
      fileIds: chatFilesRef.value.filter((f) => f.isImported).map((f) => f.id),
    })
  }
}

// on mount, mark the conversation as read
watch(
  [selectedConversationIdRef, conversationRef],
  () => {
    // if conversation selected and the user hasn't seen the conversation yet, mark it as read
    if (
      selectedConversationIdRef.value &&
      conversationRef.value &&
      !conversationRef.value.users.find((u) => u.id === accountRef.value?.id)?.hasSeen
    ) {
      markConversationAsRead({ conversationId: selectedConversationIdRef.value })
    }
  },
  { immediate: true, flush: 'post' },
)

// open new session modal
function openNewSessionModal() {
  console.log(conversationRef.value?.groupId)
  if (conversationRef.value?.groupId) {
    groupsStore.setTempSelectedGroupId(conversationRef.value?.groupId)
  }
  modalsStore.toggleModal('new-session', true)
}

onBeforeUnmount(() => {
  if (
    temporaryConversationIdRef.value &&
    !hasMessageBeenSentRef.value &&
    isTemporaryConversation(temporaryConversationIdRef.value)
  ) {
    removeTemporaryConversation(temporaryConversationIdRef.value)
    temporaryConversationSendToRef.value = null
    temporaryConversationProgramIdRef.value = null
  }
})
</script>

<style lang="scss">
@import './messages-block.scss';
</style>
